import { v4 as uuid } from 'uuid';
import type * as zod from 'zod';

import {
  deleteCodeBlockFilesOnDisk,
  readCodeBlocksOutputOnDisk,
  saveCodeBlocksOnDisk,
} from '@sb/local-build-data';
import * as log from '@sb/log';
import { convertPythonExpression } from '@sb/remote-control/util/expressions/v2';
import { FailureKind } from '@sb/routine-runner';

import Step, { type StepPlayArguments } from '../Step';

import Arguments from './Arguments';
import Variables from './Variables';

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

const ns = log.namespace('CodeBlockStep');

/** Longest id prefix kept in a run's file names — enough to identify, short
 * enough to leave room for the uuid within a filesystem name limit. */
const MAX_ID_PREFIX_LENGTH = 40;

/**
 * Reduce a caller-supplied id to characters that are safe in a file name.
 * Falls back to a constant when nothing survives, so a name never begins with
 * the separator.
 */
function sanitizeForFileName(id: string): string {
  const safe = id.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, MAX_ID_PREFIX_LENGTH);

  return safe === '' ? 'codeblock' : safe;
}

export default class CodeBlockStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  protected initializeVariableState(): void {
    this.variables = { stdout: '', stdoutTimestamp: undefined };
  }

  private runningIdentifier: string | null = null;

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    if (!this.routineContext.loadedRoutineState) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'CodeBlock',
        },
        failureReason: 'Routine State not found',
      });
    }

    // Convert the code back to user-readable form (ID → name) for
    // stacktraces. The map resolves identifiers from the routine that
    // owns this CodeBlock (loaded routine or calling sub) plus workspace
    // globals — see `RoutineContext.getIdNameMapForRoutine`.
    const idNameMap = this.routineContext.getIdNameMapForRoutine(
      this.routineFrame.routineId,
    );

    const { code } = convertPythonExpression.withIdNameMap.toUser(
      this.args.codeBlockCode,
      idNameMap,
    );

    // clear out routine runner preflight state
    const routineRunnerState = this.routineContext.getRoutineRunnerState();

    if (routineRunnerState.kind === 'RoutineRunning') {
      log.info(
        ns`runCodeblock`,
        'Setting routine runner preflight state to false',
        {
          routineRunnerState,
        },
      );

      this.routineContext.setRoutineRunnerState({
        ...routineRunnerState,
        isPreflightTestRun: false,
        readyToBeginMotion: true,
        shouldCheckGuidedMode: false,
        shouldNextArmMoveBeGuidedMode: false,
      });
    }

    // Per-execution identity for everything this run touches on disk and on
    // the wire. Naming by `codeBlockId` alone means two live instances of the
    // same step — which per-call subroutine frames make possible — share one
    // script file and one pair of output files, so each can read the other's
    // stdout as its own. It also gives the started/exited events something
    // unique to correlate on, since the command name alone matches every code
    // block execution on the robot.
    //
    // The step's own id leads, so a file left behind by a killed process still
    // says which code block wrote it and a single grep spans step logs,
    // host-command logs and disk. It is sanitized rather than interpolated:
    // `codeBlockId` is an unconstrained string that ends up inside a
    // filesystem path, so anything outside this set must not survive into it.
    const runId = `${sanitizeForFileName(this.args.codeBlockId)}-${uuid()}`;

    try {
      // create code block in local folder
      await saveCodeBlocksOnDisk(runId, code);
    } catch (err) {
      // `writeFile` creates and truncates before it writes, so a failure
      // partway through still leaves a file behind — under a name no later
      // run will ever mint again, and nothing else prunes this directory.
      await this.discardRunFiles(runId);

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'CodeBlock',
        },
        error: err,
        failureReason: 'Could not access CodeBlock folder',
      });
    }

    let tokenId: string | null = null;
    let robotToken: string | null = null;

    try {
      // Create an ephemeral token for this code block execution
      ({ tokenId, token: robotToken } =
        await this.routineContext.createRobotToken('Code Block', 'code_block'));

      // Map this execution's token to our frame so the in-process robot
      // API resolves the container's variable requests against this call
      // scope.
      this.routineContext.registerExecutionScope({
        token: robotToken,
        tokenId,
        frame: this.routineFrame,
      });
    } catch (err) {
      await this.discardRunFiles(runId);

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'CodeBlock',
        },
        error: err,
        failureReason: 'Failed to create robot token',
      });
    }

    let stdout = '';

    try {
      log.info(ns`runCodeblock`, `Running codeblock with runHostCommand`, {
        runId,
      });

      await this.routineContext.runHostCommand(
        {
          command: 'codeBlocks',
          codeBlockPath: runId,
          routineId: this.routineFrame.routineId,
          robotToken: robotToken ?? '',
          contextKind: 'code-block-step',
          robotKind: this.routineContext.robot.robotKind,
        },
        {
          onStarted: (e) => {
            log.info(
              ns`runCodeblock.started`,
              `Codeblock started: id=${e.identifier}`,
              { runId },
            );

            this.runningIdentifier = e.identifier;
          },
          onExited: (e) => {
            log.info(
              ns`runCodeblock.exited`,
              `Codeblock exited: id=${e.identifier}`,
              { runId },
            );

            this.runningIdentifier = null;
          },
          // set to number max of signed 32-bit integer
          timeout: -1,
        },
      );

      // Read inside the try so the output is captured before the `finally`
      // removes this run's files.
      // For now, not limiting the character output (e.g. 2000 characters). This could turn out to be an issue down the road.
      stdout = await readCodeBlocksOutputOnDisk(runId, 'stdout');
    } catch (err) {
      const stderr = await readCodeBlocksOutputOnDisk(runId, 'stderr');

      log.error(ns`runCodeblock`, 'Codeblock stderr', {
        runId,
        stderr,
        stderrTimestamp: new Date(),
      });

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'CodeBlock',
          stackTrace: stderr !== '' ? stderr : undefined,
        },
        error: err,
        failureReason: 'An error occurred while running the CodeBlock',
      });
    } finally {
      // Revoke BEFORE unregistering — the ordering is load-bearing. A
      // straggling container request during teardown then sees a revoked
      // but still-scoped token (resolves to the correct frame) rather
      // than a valid token that has fallen back to the root scope.
      try {
        if (tokenId) {
          await this.routineContext.revokeRobotToken(tokenId);
        }
      } catch (e) {
        log.warn(ns`token.revoke.failed`, 'Failed to revoke code block token', {
          tokenId,
          error: e,
        });
        // Do not throw — best-effort cleanup; token is revoked on next code block run or robot reboot
      }

      if (robotToken) {
        this.routineContext.unregisterExecutionScope(robotToken);
      }

      await this.discardRunFiles(runId);
    }

    log.info(ns`runCodeblock`, 'Codeblock stdout', {
      runId,
      stdout,
      stdoutTimestamp: new Date(),
    });

    this.setVariable('stdout', stdout);
    this.setVariable('stdoutTimestamp', new Date());
  }

  /**
   * Best-effort removal of one execution's script and output. Per-execution
   * file names mean nothing else will ever collect them.
   */
  private async discardRunFiles(runId: string): Promise<void> {
    try {
      await deleteCodeBlockFilesOnDisk(runId);
    } catch (err) {
      log.warn(ns`cleanup`, 'Failed to remove code block run files', {
        runId,
        error: err,
      });
    }
  }

  protected async _pause({ fail }: StepPlayArguments): Promise<void> {
    fail({
      failure: {
        kind: FailureKind.StepPlayFailure,
        stepKind: 'CodeBlock',
      },
      failureReason:
        'Must restart the routine after a pause during a codeblock execution',
    });
  }

  protected async _stop({ fail }: StepPlayArguments): Promise<void> {
    log.info(ns`stop`, `Stopping codeblock: ${this.runningIdentifier}`);

    if (this.runningIdentifier) {
      try {
        const result = await this.routineContext.stopHostCommand?.(
          this.runningIdentifier,
        );

        log.info(ns`stop`, `Stopped codeblock`, {
          id: this.runningIdentifier,
          result,
        });
      } catch (err) {
        log.error(ns`stop`, `Failed to stop codeblock`, {
          id: this.runningIdentifier,
          error: String(err),
        });

        // If we fail to stop the codeblock, we should fail the step
        fail({
          failure: {
            kind: FailureKind.StepPlayFailure,
            stepKind: 'CodeBlock',
          },
          failureReason: 'Failed to stop codeblock',
        });
      } finally {
        this.runningIdentifier = null;
      }
    }
  }

  protected async _onCleanup({ fail }: StepPlayArguments): Promise<void> {
    await this.stop({ fail });
  }

  public hasSelfTimeout(): boolean {
    return false;
  }
}
